/**
 * CONNECTOR DUPLICATION PREVENTION SYSTEM
 * 
 * Prevents the creation of duplicate connector walls during extrusion operations.
 * This addresses Phase 2: Connector Wall Duplication critical issues.
 */

import { Wall } from '../types';

export interface ConnectorExistenceCheck {
  exists: boolean;
  existingConnector?: Wall;
  reason?: string;
}

/**
 * Checks if a connector with identical or near-identical coordinates already exists
 */
export function checkConnectorExists(
  proposedConnector: Partial<Wall>,
  existingWalls: Wall[],
  tolerance: number = 1.0
): ConnectorExistenceCheck {
  
  if (!proposedConnector.start || !proposedConnector.end) {
    return { exists: false, reason: 'Invalid proposed connector coordinates' };
  }

  const existingConnectors = existingWalls.filter(w => (w as any).isExtrusionConnector);
  
  for (const existing of existingConnectors) {
    // Check for identical coordinates (same direction)
    const sameDirection = 
      Math.abs(existing.start.x - proposedConnector.start.x) < tolerance &&
      Math.abs(existing.start.y - proposedConnector.start.y) < tolerance &&
      Math.abs(existing.end.x - proposedConnector.end.x) < tolerance &&
      Math.abs(existing.end.y - proposedConnector.end.y) < tolerance;

    // Check for reversed coordinates (opposite direction)
    const reversedDirection = 
      Math.abs(existing.start.x - proposedConnector.end.x) < tolerance &&
      Math.abs(existing.start.y - proposedConnector.end.y) < tolerance &&
      Math.abs(existing.end.x - proposedConnector.start.x) < tolerance &&
      Math.abs(existing.end.y - proposedConnector.start.y) < tolerance;

    if (sameDirection || reversedDirection) {
      console.log('🚨 DUPLICATE_CONNECTOR_DETECTED', {
        proposedConnector: {
          start: proposedConnector.start,
          end: proposedConnector.end,
          parentWallId: (proposedConnector as any).parentWallId
        },
        existingConnector: {
          tempId: (existing as any).tempId,
          start: existing.start,
          end: existing.end,
          parentWallId: (existing as any).parentWallId
        },
        matchType: sameDirection ? 'SAME_DIRECTION' : 'REVERSED_DIRECTION',
        tolerance
      });

      return {
        exists: true,
        existingConnector: existing,
        reason: `Duplicate connector found with ${sameDirection ? 'same' : 'reversed'} direction`
      };
    }
  }

  return { exists: false, reason: 'No duplicate connector found' };
}

/**
 * Filters out duplicate connectors from a list of connectors to add
 */
export function filterDuplicateConnectors(
  connectorsToAdd: Wall[],
  existingWalls: Wall[],
  tolerance: number = 1.0
): { uniqueConnectors: Wall[], duplicatesFound: number, duplicateDetails: any[] } {
  
  const uniqueConnectors: Wall[] = [];
  const duplicateDetails: any[] = [];
  let duplicatesFound = 0;

  for (const connector of connectorsToAdd) {
    const existenceCheck = checkConnectorExists(connector, existingWalls, tolerance);
    
    if (existenceCheck.exists) {
      duplicatesFound++;
      duplicateDetails.push({
        proposedConnector: {
          start: connector.start,
          end: connector.end,
          parentWallId: (connector as any).parentWallId
        },
        existingConnector: existenceCheck.existingConnector,
        reason: existenceCheck.reason
      });
      
      console.log('🚫 DUPLICATE_CONNECTOR_FILTERED', {
        filteredConnector: {
          start: connector.start,
          end: connector.end,
          parentWallId: (connector as any).parentWallId
        },
        existingConnectorId: (existenceCheck.existingConnector as any)?.tempId,
        reason: existenceCheck.reason
      });
    } else {
      uniqueConnectors.push(connector);
    }
    
    // Also check against other connectors in the same batch to prevent internal duplicates
    const internalCheck = checkConnectorExists(connector, uniqueConnectors, tolerance);
    if (internalCheck.exists && uniqueConnectors.length > 0) {
      // Remove the last added connector if it's a duplicate within the batch
      uniqueConnectors.pop();
      duplicatesFound++;
      duplicateDetails.push({
        proposedConnector: {
          start: connector.start,
          end: connector.end,
          parentWallId: (connector as any).parentWallId
        },
        internalDuplicate: true,
        reason: 'Duplicate within same batch of connectors'
      });
    }
  }

  console.log('🔍 CONNECTOR_DUPLICATION_FILTER_RESULTS', {
    originalConnectorCount: connectorsToAdd.length,
    uniqueConnectorCount: uniqueConnectors.length,
    duplicatesFound,
    duplicateDetails: duplicateDetails.length
  });

  return { uniqueConnectors, duplicatesFound, duplicateDetails };
}

/**
 * Creates a safe connector with duplication prevention
 */
export function createSafeConnector(
  connectorData: Partial<Wall>,
  existingWalls: Wall[],
  tolerance: number = 1.0
): { connector: Wall | null, wasCreated: boolean, reason: string } {
  
  const existenceCheck = checkConnectorExists(connectorData, existingWalls, tolerance);
  
  if (existenceCheck.exists) {
    return {
      connector: null,
      wasCreated: false,
      reason: `Connector creation skipped: ${existenceCheck.reason}`
    };
  }

  // Generate a unique tempId for the new connector
  const tempId = `connector-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  
  const newConnector: Wall = {
    start: connectorData.start!,
    end: connectorData.end!,
    type: connectorData.type || 'wall',
    floor: connectorData.floor || 1,
    tempId,
    isExtrusionConnector: true,
    parentWallId: (connectorData as any).parentWallId,
    originatingRoomId: (connectorData as any).originatingRoomId,
    assignedRoomIds: (connectorData as any).assignedRoomIds ? [...(connectorData as any).assignedRoomIds] : undefined,
    parentRoomIds: (connectorData as any).parentRoomIds ? [...(connectorData as any).parentRoomIds] : undefined
  } as Wall;

  console.log('✅ SAFE_CONNECTOR_CREATED', {
    tempId,
    start: newConnector.start,
    end: newConnector.end,
    parentWallId: (newConnector as any).parentWallId
  });

  return {
    connector: newConnector,
    wasCreated: true,
    reason: 'New unique connector created successfully'
  };
}

/**
 * Diagnostic function to analyze existing connector duplicates
 */
export function analyzeExistingConnectorDuplicates(
  walls: Wall[],
  tolerance: number = 1.0
): { duplicateGroups: Wall[][], totalDuplicates: number, analysisDetails: any[] } {
  
  const connectors = walls.filter(w => (w as any).isExtrusionConnector);
  const duplicateGroups: Wall[][] = [];
  const analysisDetails: any[] = [];
  const processedConnectors = new Set<string>();

  for (let i = 0; i < connectors.length; i++) {
    const connector1 = connectors[i];
    const connector1Id = (connector1 as any).tempId;
    
    if (processedConnectors.has(connector1Id)) continue;

    const duplicateGroup: Wall[] = [connector1];
    processedConnectors.add(connector1Id);

    for (let j = i + 1; j < connectors.length; j++) {
      const connector2 = connectors[j];
      const connector2Id = (connector2 as any).tempId;
      
      if (processedConnectors.has(connector2Id)) continue;

      const existenceCheck = checkConnectorExists(connector1, [connector2], tolerance);
      
      if (existenceCheck.exists) {
        duplicateGroup.push(connector2);
        processedConnectors.add(connector2Id);
        
        analysisDetails.push({
          connector1Id,
          connector2Id,
          connector1Coords: `${connector1.start.x},${connector1.start.y} to ${connector1.end.x},${connector1.end.y}`,
          connector2Coords: `${connector2.start.x},${connector2.start.y} to ${connector2.end.x},${connector2.end.y}`,
          reason: existenceCheck.reason
        });
      }
    }

    if (duplicateGroup.length > 1) {
      duplicateGroups.push(duplicateGroup);
    }
  }

  const totalDuplicates = duplicateGroups.reduce((sum, group) => sum + group.length - 1, 0);

  console.log('🔍 EXISTING_CONNECTOR_DUPLICATE_ANALYSIS', {
    totalConnectors: connectors.length,
    duplicateGroups: duplicateGroups.length,
    totalDuplicates,
    analysisDetails: analysisDetails.length
  });

  return { duplicateGroups, totalDuplicates, analysisDetails };
}
